import React, { useMemo, useState } from 'react';
import { diffLines } from 'diff';
import styled from 'styled-components';

import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';

import MarkdownBody from './MarkdownBody';

// Two-column line diff for candidate review: published body on the left,
// proposed body on the right. Added lines highlight on the right, removed
// lines on the left, using the existing status tokens so both themes work.

const Columns = styled.div`
  display: flex;
  gap: 16px;
  align-items: stretch;
`;

const Column = styled.div`
  flex: 1;
  min-width: 0;
  border: 1px solid var(--color-border-secondary);
  border-radius: 8px;
  overflow: hidden;
`;

const ColumnHeader = styled.div`
  padding: 8px 12px;
  border-bottom: 1px solid var(--color-border-secondary);
  background-color: var(--color-bg-secondary);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 20px;
`;

const PreviewToggleButton = styled.button`
  display: flex;
  align-items: center;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;
`;

// The rendered-markdown preview reuses the diff column's padding so it lines up
// with the raw <pre> view it replaces.
const PreviewDiv = styled.div`
  padding: 12px;
`;

const Lines = styled.pre`
  margin: 0;
  padding: 12px;
  font-size: 12px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  color: var(--color-text-primary);
`;

const Line = styled.span<{ tone: 'added' | 'removed' | 'unchanged' }>`
  display: block;
  ${(props) =>
    props.tone === 'added'
      ? 'background-color: var(--color-status-success-bg);'
      : props.tone === 'removed'
        ? 'background-color: var(--color-status-danger-bg);'
        : ''}
`;

interface DiffLine {
  text: string;
  tone: 'added' | 'removed' | 'unchanged';
}

const toLines = (value: string): string[] => {
  const lines = value.split('\n');
  // A trailing newline yields a phantom empty last line — drop it.
  if (lines.length > 0 && lines[lines.length - 1] === '') {
    lines.pop();
  }
  return lines;
};

const SideBySideDiff = ({
  currentBody,
  proposedBody,
  currentLabel,
  proposedLabel,
}: {
  currentBody: string;
  proposedBody: string;
  currentLabel: string;
  proposedLabel: string;
}) => {
  const [isPreview, setIsPreview] = useState<boolean>(false);

  const { left, right } = useMemo(() => {
    const changes = diffLines(currentBody, proposedBody);
    const leftLines: DiffLine[] = [];
    const rightLines: DiffLine[] = [];
    changes.forEach((change) => {
      const lines = toLines(change.value);
      if (change.added) {
        lines.forEach((text) => rightLines.push({ text, tone: 'added' }));
      } else if (change.removed) {
        lines.forEach((text) => leftLines.push({ text, tone: 'removed' }));
      } else {
        lines.forEach((text) => {
          leftLines.push({ text, tone: 'unchanged' });
          rightLines.push({ text, tone: 'unchanged' });
        });
      }
    });
    return { left: leftLines, right: rightLines };
  }, [currentBody, proposedBody]);

  return (
    <Columns data-testid="side-by-side-diff">
      <Column>
        <ColumnHeader>
          <Typography.Body type="Label">{currentLabel}</Typography.Body>
        </ColumnHeader>
        <Lines>
          {left.map((line, i) => (
            <Line key={`l-${i}`} tone={line.tone}>
              {line.text === '' ? ' ' : line.text}
            </Line>
          ))}
        </Lines>
      </Column>
      <Column>
        <ColumnHeader>
          <Typography.Body type="Label">{proposedLabel}</Typography.Body>
          <PreviewToggleButton
            type="button"
            onClick={() => setIsPreview((prev) => !prev)}
            title={isPreview ? 'Show raw diff' : 'Preview markdown'}
            aria-label={isPreview ? 'Show raw diff' : 'Preview markdown'}
            aria-pressed={isPreview}
            data-testid="diff-preview-toggle"
          >
            <Icon
              icon={isPreview ? 'visibilityOff' : 'eye'}
              width={16}
              height={16}
              color="var(--color-text-secondary)"
            />
          </PreviewToggleButton>
        </ColumnHeader>
        {isPreview ? (
          <PreviewDiv data-testid="diff-preview">
            <MarkdownBody>{proposedBody}</MarkdownBody>
          </PreviewDiv>
        ) : (
          <Lines>
            {right.map((line, i) => (
              <Line key={`r-${i}`} tone={line.tone}>
                {line.text === '' ? ' ' : line.text}
              </Line>
            ))}
          </Lines>
        )}
      </Column>
    </Columns>
  );
};

export default SideBySideDiff;
